import type { BillingPanelData } from '@dreamquiz/shared';

/**
 * Klickbares Billing-Panel für normale Nutzer (Wunsch Niggo 2026-09-13:
 * "wie das Admin-Screen, aber für Rundenpakete/Abos"). Gleiches Muster wie
 * adminPanel.ts: zeigt nur an und reicht Klicks weiter, der Server
 * entscheidet/prüft alles (siehe billing/*.ts, net/socketAdapter.ts).
 */
export interface BillingPanelActions {
  refresh(): void;
  buyPackage(packageId: string): void;
  buyPlan(planId: string): void;
  cancelSubscription(): void;
}

function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} fehlt im HTML`);
  return el as T;
}

function formatCents(cents: number): string {
  return `${(cents / 100).toFixed(2)} €`;
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Schätzung, wie viele KI-Runden noch drin sind (Wunsch Niggo 2026-09-14) - `null` heißt "noch keine Datenbasis" (z. B. kurz nach Serverstart), kein Fehler. */
function formatEstimatedRounds(rounds: number | null): string {
  return rounds === null ? 'noch unklar' : `~${rounds} Runden`;
}

/** Wunsch Niggo 2026-09-14: Kosten-Kacheln farblich einordnen - nur für App-Admins hier im Billing-Panel (nicht im Admin-Panel-Kosten-Tab, das war ein Missverständnis). Schwellen in $ (Anbieter-Preise). */
function costTier(usd: number): { tier: 'green' | 'yellow' | 'red'; hint: string } {
  if (usd < 5) return { tier: 'green', hint: 'im Rahmen' };
  if (usd < 10) return { tier: 'yellow', hint: 'Geld aufpassen' };
  return { tier: 'red', hint: 'zu viel bro' };
}

const TRANSACTION_LABELS: Record<string, string> = {
  package: 'Rundenpaket',
  subscription_initial: 'Abo (Start)',
  subscription_renewal: 'Abo (Verlängerung)',
};

export class BillingPanel {
  private readonly overlay = must('billing-overlay');

  constructor(private readonly actions: BillingPanelActions) {
    must('billing-close').addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    });
  }

  get isOpen(): boolean {
    return !this.overlay.hidden;
  }

  open(): void {
    this.overlay.hidden = false;
    this.actions.refresh();
  }

  close(): void {
    this.overlay.hidden = true;
  }

  render(data: BillingPanelData): void {
    must('billing-disabled-hint').hidden = data.enabled;

    const rows: [string, string, ('green' | 'yellow' | 'red')?, string?][] = [
      ['Rundenguthaben', `${data.roundCredits} Runden`],
      ['Gratis-Budget', data.isAdmin ? 'unbegrenzt (App-Admin)' : formatEstimatedRounds(data.freeBudgetEstimatedRounds)],
    ];
    if (data.subscriptionBudget) {
      rows.push(
        ['Abo-Budget (Schätzung)', data.isAdmin ? 'unbegrenzt (App-Admin)' : formatEstimatedRounds(data.subscriptionBudget.estimatedRounds)],
        ['Abo-Budget (real)', `${data.subscriptionBudget.remainingUsd.toFixed(2)} $`],
      );
    }
    // Farbcodierte Kosten-Übersicht NUR für App-Admins (Wunsch Niggo
    // 2026-09-14) - normale Budget-Schätzungen sind für sie ja bedeutungslos
    // (siehe "unbegrenzt" oben), stattdessen ihr eigener tatsächlicher
    // Verbrauch als Selbstkontrolle.
    if (data.isAdmin && data.adminCost) {
      const cost24h = costTier(data.adminCost.totalCost);
      const costAll = costTier(data.adminCost.allTimeCost);
      rows.push(
        ['KI-Kosten (24h)', `~$${data.adminCost.totalCost.toFixed(4)}`, cost24h.tier, cost24h.hint],
        ['KI-Kosten (Insgesamt)', `~$${data.adminCost.allTimeCost.toFixed(4)}`, costAll.tier, costAll.hint],
      );
    }
    this.renderStats('billing-balance-grid', rows);
    this.renderSubscriptionBox(data);
    this.renderPackages(data);
    this.renderPlans(data);
    this.renderHistory(data.history);
  }

  private renderSubscriptionBox(data: BillingPanelData): void {
    const box = must('billing-subscription-box');
    box.replaceChildren();
    if (!data.subscription) {
      const p = document.createElement('p');
      p.className = 'admin-hint';
      p.textContent = 'Kein aktives Abo.';
      box.appendChild(p);
      return;
    }

    const sub = data.subscription;
    const p = document.createElement('p');
    if (sub.status === 'active') {
      p.textContent = `Abo "${sub.planName}" aktiv - verlängert sich automatisch am ${formatDate(sub.currentPeriodEnd)}.`;
    } else if (sub.status === 'cancelled') {
      p.textContent = `Abo "${sub.planName}" gekündigt - Vorteile bleiben bis ${formatDate(sub.currentPeriodEnd)} erhalten.`;
    } else {
      p.textContent = `Abo "${sub.planName}" beendet.`;
    }
    box.appendChild(p);

    if (sub.status === 'active') {
      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'admin-btn';
      cancelBtn.textContent = 'Abo kündigen';
      cancelBtn.addEventListener('click', () => {
        // Sofortige Nachfrage direkt hier statt eines E-Mail-Umwegs (Wunsch
        // Niggo 2026-09-13: schnell und unkompliziert kündbar, § 312k BGB) -
        // die Vorteile laufen trotzdem bis zum Ende der Periode weiter, siehe
        // Text oben.
        if (window.confirm(`Abo "${sub.planName}" wirklich kündigen? Die Vorteile bleiben bis ${formatDate(sub.currentPeriodEnd)} erhalten.`)) {
          this.actions.cancelSubscription();
        }
      });
      box.appendChild(cancelBtn);
    }
  }

  private renderPackages(data: BillingPanelData): void {
    const container = must('billing-packages');
    container.replaceChildren();
    if (!data.packages.length) {
      container.appendChild(this.hint('Aktuell keine Rundenpakete im Angebot.'));
      return;
    }
    for (const pkg of data.packages) {
      const row = document.createElement('div');
      row.className = 'admin-billing-row billing-offer';

      const label = document.createElement('div');
      label.className = 'billing-offer-label';
      const name = document.createElement('b');
      name.textContent = pkg.name;
      label.appendChild(name);
      if (pkg.description) {
        const desc = document.createElement('small');
        desc.textContent = pkg.description;
        label.appendChild(document.createElement('br'));
        label.appendChild(desc);
      }
      row.appendChild(label);

      const price = document.createElement('span');
      price.textContent = formatCents(pkg.priceCents);
      row.appendChild(price);

      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'admin-btn';
      buy.textContent = 'Kaufen';
      buy.disabled = !data.enabled;
      buy.addEventListener('click', () => this.actions.buyPackage(pkg.id));
      row.appendChild(buy);

      container.appendChild(row);
    }
  }

  private renderPlans(data: BillingPanelData): void {
    const container = must('billing-plans');
    container.replaceChildren();
    if (!data.plans.length) {
      container.appendChild(this.hint('Aktuell keine Abos im Angebot.'));
      return;
    }
    const hasRunningSubscription = data.subscription !== null && data.subscription.status !== 'deactivated';

    for (const plan of data.plans) {
      const row = document.createElement('div');
      row.className = 'admin-billing-row billing-offer';

      const label = document.createElement('div');
      label.className = 'billing-offer-label';
      const name = document.createElement('b');
      name.textContent = plan.name;
      label.appendChild(name);
      const desc = document.createElement('small');
      desc.textContent = `${plan.description ? `${plan.description} · ` : ''}+$${plan.monthlyBudgetBonusUsd.toFixed(2)} KI-Budget/Monat`;
      label.appendChild(document.createElement('br'));
      label.appendChild(desc);
      if (hasRunningSubscription) {
        const warn = document.createElement('small');
        warn.className = 'billing-offer-warn';
        warn.textContent = 'Du hast bereits ein Abo - bitte kündige dieses erst.';
        label.appendChild(document.createElement('br'));
        label.appendChild(warn);
      }
      row.appendChild(label);

      const price = document.createElement('span');
      price.textContent = `${formatCents(plan.priceCents)} / 30 Tage`;
      row.appendChild(price);

      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'admin-btn';
      buy.textContent = 'Abonnieren';
      buy.disabled = !data.enabled || hasRunningSubscription;
      buy.title = hasRunningSubscription ? 'Erst das laufende Abo kündigen, dann kann ein neues gestartet werden.' : '';
      buy.addEventListener('click', () => this.actions.buyPlan(plan.id));
      row.appendChild(buy);

      container.appendChild(row);
    }
  }

  private renderHistory(history: BillingPanelData['history']): void {
    const list = must('billing-history');
    list.replaceChildren();
    if (!history.length) {
      const li = document.createElement('li');
      li.style.listStyle = 'none';
      li.style.color = 'var(--mu)';
      li.textContent = 'Noch keine Zahlungen.';
      list.appendChild(li);
      return;
    }
    for (const t of history) {
      const li = document.createElement('li');
      li.style.listStyle = 'none';
      li.textContent = `${formatDate(t.createdAt)} · ${t.label} (${TRANSACTION_LABELS[t.type] ?? t.type}) · ${formatCents(t.amountCents)}`;
      list.appendChild(li);
    }
  }

  private hint(text: string): HTMLElement {
    const p = document.createElement('p');
    p.className = 'admin-hint';
    p.textContent = text;
    return p;
  }

  private renderStats(
    gridId: string,
    rows: [label: string, value: string, tier?: 'green' | 'yellow' | 'red', hint?: string][],
  ): void {
    const grid = must(gridId);
    grid.replaceChildren();
    for (const [label, value, tier, hint] of rows) {
      const cell = document.createElement('div');
      cell.className = tier ? `admin-stat admin-stat-${tier}` : 'admin-stat';
      const v = document.createElement('b');
      v.textContent = value;
      const l = document.createElement('small');
      l.textContent = label;
      cell.appendChild(v);
      cell.appendChild(l);
      if (hint) {
        const h = document.createElement('small');
        h.className = 'admin-stat-hint';
        h.textContent = hint;
        cell.appendChild(h);
      }
      grid.appendChild(cell);
    }
  }
}
